import { Component, forwardRef, input, OnInit,  signal,  viewChild } from '@angular/core';
import { AutocompleteComponent, InputFieldComponent } from '@nagel-form';
import { AutoCompleteField, FieldOption } from '@nagel-types';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    FormGroup,
    FormGroupDirective,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    NgForm,
    ReactiveFormsModule,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ErrorStateMatcher } from '@angular/material/core';
import { DRIVER_COUNTRY_CODE_OPTIONS, getIsValidCountryCode } from '@nagel-utils';
import { INVALID_PHONE_NUMBER_ERROR } from '@nagel-consts';

export class PhoneFieldsErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        if (form?.valid)
        {
            return false;
        }

        return !!(form?.hasError(INVALID_PHONE_NUMBER_ERROR)
            && (form?.dirty || form?.touched)
            && control?.dirty);
    }
}

@Component({
    selector: 'lib-phone-number',
    imports: [
        InputFieldComponent,
        ReactiveFormsModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        AutocompleteComponent
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneNumberComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PhoneNumberComponent),
            multi: true
        }
    ],
    templateUrl: './phone-number.component.html',
    styleUrl: './phone-number.component.scss',
})
export class PhoneNumberComponent implements ControlValueAccessor, Validator, OnInit {
    countryCodeControl = viewChild.required(AutocompleteComponent);
    phoneInputControl = viewChild.required(InputFieldComponent);
    handleBlur = input<() => void>(() => { });
    form = input.required<FormGroup>();
    finishedEdit = signal<boolean>(true);

    onChange: any = () => {};
    onTouched: any = () => {};
    // phoneErrorStateMatcher: ErrorStateMatcher = new PhoneFieldsErrorStateMatcher();
    autocompleteFieldConfig = {} as AutoCompleteField<FieldOption>;

    constructor() {}

    ngOnInit() {

        this.form().valueChanges.subscribe(value => {
            this.finishedEdit.set(false);
            this.onChange(value);
            this.onTouched();
        });

        this.autocompleteFieldConfig =  new AutoCompleteField<FieldOption>({
            key: 'countryCode',
            label: $localize`Country code`,
            readonly: false,
            fieldName: 'countryCode',
            placeholder: '',
            options: DRIVER_COUNTRY_CODE_OPTIONS,
            onSelect: this.onCountryCodeSelect,
            onBlur: this.onCountryCodeBlur
        });
    }

    phoneErrorStateMatcher: ErrorStateMatcher = {
        isErrorState: (control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean => {
            if (form?.valid)
            {
                return false;
            }

            const hasPhoneFormError = form?.hasError(INVALID_PHONE_NUMBER_ERROR)
                && this.finishedEdit()
                && (form?.dirty || form?.touched);

            return !!(hasPhoneFormError || control?.errors);
        }
    };

    writeValue(value: any): void {
        if (value) { this.form().patchValue(value, { emitEvent: false }); }
    }

    registerOnChange(fn: any): void { this.onChange = fn; }
    registerOnTouched(fn: any): void { this.onTouched = fn; }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.form().disable() : this.form().enable();
    }

    validate = (control: AbstractControl): ValidationErrors | null => {
        return this.form().hasError(INVALID_PHONE_NUMBER_ERROR) && control?.dirty
            ? { invalidPhoneNumber: true }
            : null;
    };

    onCountryCodeBlur = () => {
        const countryCodeControl = this.form().get('countryCode');
        const countryCodeValue = countryCodeControl?.value?.value || '';
        const numberValue = this.form().get('number')?.value || '';

        if (countryCodeValue && !numberValue) {
            requestAnimationFrame(() => {
                this.phoneInputControl()?.focus();
            });
        } else {
            this.finishedEdit.set(true);
        }

        countryCodeControl?.markAsTouched();
        countryCodeControl?.updateValueAndValidity();
    };

    onCountryCodeSelect = () => {
        const phoneValue = this.form().get('number')?.value || '';

        if (!phoneValue) {
            requestAnimationFrame(() => {
                this.phoneInputControl()?.focus();
            });
        } else {
            this.handleBlurInternaly();
        }
    };

    onPhoneInputBlur = () => {
        const numberControl = this.form().get('number');
        const countryCodeValue = this.form().get('countryCode')?.value?.value || '';

        if (!countryCodeValue) {
            this.countryCodeControl()?.focus();
        } else {
            numberControl?.markAsTouched();
            numberControl?.updateValueAndValidity();
            this.handleBlurInternaly();
        }
    };

    getErrorMessage = () => {
        let result = '';

        if (this.form().hasError(INVALID_PHONE_NUMBER_ERROR)
                && this.finishedEdit()
                && (this.form().touched || this.form().dirty)
        ) {
            result = $localize`The combination of country code and mobile number is not valid.`;
        }

        return result;
    };

    handleBlurInternaly = () => {
        const blurHandlerFn = this.handleBlur();

        this.finishedEdit.set(true);

        if (blurHandlerFn) {
            blurHandlerFn();
        }
    };

    getShouldShowCountryCodeWarning = () => {
        const countryCodeValue = this.form().get('countryCode')?.value?.value || '';
        const phoneValue = this.form().get('number')?.value || '';

        return phoneValue
            && !getIsValidCountryCode(countryCodeValue)
            && this.form().untouched
            && (this.form().pristine || this.form().valid);
    };

    getIconName = () => {
        return this.getShouldShowCountryCodeWarning()
            ? 'warning_icon'
            : undefined;
    };

    getWarningMessage = () => {
        return this.getShouldShowCountryCodeWarning()
            ? $localize`Select a country code`
            : '';
    };

    reset = () => {
        this.form().reset({
            countryCode: {value: '', viewValue: ''},
            number:  ''
        });
        this.form().markAsPristine();
        this.form().markAsUntouched();
    };
}
